import { Box, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import { Link, useLocation } from "@tanstack/react-router";
import {
  Braces,
  Building2,
  ChevronDown,
  FolderTree,
  KeyRound,
  LayoutGrid,
  LogOut,
  type LucideIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Route,
  ScrollText,
  ShieldCheck,
  Tags,
  UserCog,
  UserRound,
  Users,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Tenant } from "../data/api";
import {
  GROUPS,
  type PageDef,
  USER_FLOWS,
  USER_MANAGEMENT,
  labelOf,
  sectionOf,
} from "../pages/pages";
import { useTokens } from "../ui/theme/theme";

// ---
// Sidebar
//
// Grouped by *scope*: Platform operates across tenants, and everything under the tenant group
// operates inside the one picked at the head of that group.
// The switcher sits beside what it governs, so what a click affects is on screen.
//
// The rail sits on the desk rather than on a surface of its own: the page is the card, and a
// second bordered panel beside it would compete with it for the eye.
// ---

/**
 * One glyph per page that stands on its own.
 *
 * Here rather than in the page table, which a node test imports: an icon is this component's
 * business, and pulling an icon library into that module would be paid for on every run.
 *
 * A page under a heading has none, wide or narrow: the heading carries the glyph for the group,
 * and four of them stacked would say four things where there is one.
 */
const ICONS: Record<string, LucideIcon> = {
  platform: Building2,
  applications: LayoutGrid,
  apis: Braces,
  projects: FolderTree,
  claims: Tags,
  logs: ScrollText,
  tokens: KeyRound,
};

/**
 * A heading's glyph, which must differ from every page under it.
 *
 * Narrowed, the heading shows its glyph and the pages show theirs. Share one between them and
 * the same picture appears at two heights — which reads as the icon having moved when the
 * heading is the only one of the two that is hidden.
 */
const SECTION_ICONS: Record<string, LucideIcon> = {
  [USER_MANAGEMENT]: UserCog,
  [USER_FLOWS]: Route,
};

/**
 * The page's name, read from where we are rather than passed down.
 *
 * A hook, and named like one: it reads the location, so calling it after an early return changes
 * how many hooks a render used — which React notices and refuses.
 */
export function usePageLabel(): string {
  const segment = useLocation().pathname.split("/").filter(Boolean).pop() ?? "";
  return labelOf(segment);
}

/** The heading the current page sits under, when it sits under one. */
export function usePageSection(): string | undefined {
  const segment = useLocation().pathname.split("/").filter(Boolean).pop() ?? "";
  return sectionOf(segment);
}

/** Whether the current place is inside a tenant, which decides the breadcrumb's shape. */
export function useIsTenantPath(): boolean {
  return useLocation().pathname.startsWith("/tenants/");
}

/** The label's line box, which a rule has to fill exactly so nothing below it moves. */
const EYEBROW_LINE = 14;

/**
 * The eyebrow over a group: mono and spaced, so it reads as a label and not as a row.
 *
 * Narrowed to glyphs there is no room for the word, so the group is marked by a rule instead —
 * in the same box, of the same height. Narrowing the rail changes what a thing says, never where
 * it sits, or every glyph lands somewhere new and the eye has to find them all again.
 */
function Eyebrow({
  children,
  first,
  hidden,
}: {
  children: string;
  first: boolean;
  hidden: boolean;
}): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      aria-hidden={hidden || undefined}
      sx={{
        px: 2,
        pt: first ? 1.5 : 4.5,
        pb: 1.5,
        fontFamily: tokens.font.mono,
        fontSize: 10.5,
        lineHeight: `${EYEBROW_LINE}px`,
        fontWeight: tokens.weight.medium,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: tokens.ink.muted,
      }}
    >
      {hidden ? (
        <Box sx={{ height: `${EYEBROW_LINE}px`, display: "flex", alignItems: "center" }}>
          <Box sx={{ width: "100%", height: "1px", bgcolor: tokens.line.strong }} />
        </Box>
      ) : (
        children
      )}
    </Box>
  );
}

/** A row in the rail: a page, a heading, or the account and the collapse control at its foot. */
const row = {
  display: "flex",
  alignItems: "center",
  gap: 2.5,
  width: "100%",
  height: 32,
  px: 2,
  border: 0,
  borderRadius: "8px",
  font: "inherit",
  fontSize: 13.5,
  textAlign: "left",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
} as const;

export function Nav({
  tenant,
  tenants,
  onSelectTenant,
  scopes,
  isPlatform,
  account,
  collapsed,
  onAccount,
  onSignOut,
  onToggleCollapsed,
}: {
  tenant: string;
  tenants: Tenant[];
  /** Switching is navigation: the tenant is in the path, so the caller decides where it lands. */
  onSelectTenant: (id: string) => void;
  scopes: string[];
  isPlatform: boolean;
  account: string;
  collapsed: boolean;
  onAccount: () => void;
  onSignOut: () => void;
  onToggleCollapsed: () => void;
}): React.JSX.Element {
  const tokens = useTokens();
  const pathname = useLocation().pathname;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [picking, setPicking] = useState<HTMLElement | null>(null);
  /** What the switcher can offer: everywhere but here. Nothing to offer makes it a label. */
  const others = tenants.filter((each) => each.id !== tenant);
  /** Headings the reader has shut. Open is the default, so an empty set is everything open. */
  const [shut, setShut] = useState<ReadonlySet<string>>(() => new Set());
  const narrow = { justifyContent: "center", px: 0 } as const;

  /** Narrowed, a row is only its glyph — so its name has to be available some other way. */
  const named = (label: string, element: ReactNode): React.JSX.Element =>
    collapsed ? (
      <Tooltip title={label} placement="right">
        <span>{element}</span>
      </Tooltip>
    ) : (
      <>{element}</>
    );

  const pageRow = (entry: PageDef): React.JSX.Element => {
    // Disabled rather than hidden: a control you cannot use is still information about what
    // exists, and the server refuses regardless of what the sidebar shows.
    const allowed = entry.scope === undefined || scopes.includes(entry.scope);
    const href = entry.href ?? `/tenants/${tenant}/${entry.page}`;
    const on = pathname === href;
    const under = entry.section !== undefined;
    const Icon = ICONS[entry.page];
    // A link, not a button: a page is a place, so it should be openable in a new tab, copyable,
    // and reachable with the back button like anything else on the web.
    const inner = (
      <Box
        component={allowed ? Link : "span"}
        {...(allowed ? { to: href } : {})}
        aria-disabled={allowed ? undefined : true}
        aria-current={on ? "page" : undefined}
        sx={{
          ...row,
          ...(under ? { height: 30, fontSize: 13, ...(collapsed ? {} : { pl: 9 }) } : {}),
          ...(collapsed ? narrow : {}),
          fontWeight: on ? tokens.weight.medium : tokens.weight.regular,
          color: !allowed ? tokens.ink.faint : on ? tokens.accent.ink : tokens.ink.muted,
          bgcolor: on ? tokens.accent.soft : "transparent",
          cursor: allowed ? "pointer" : "default",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...(allowed && !on ? { "&:hover": { bgcolor: tokens.state.hover } } : {}),
          "&:focus-visible": { outline: `2px solid ${tokens.accent.base}`, outlineOffset: "-2px" },
        }}
      >
        {Icon !== undefined && (
          <Box aria-hidden sx={{ flex: "none", display: "flex" }}>
            <Icon size={18} strokeWidth={1.6} />
          </Box>
        )}
        {!collapsed && entry.label}
      </Box>
    );
    return (
      <Box component="li" key={entry.page}>
        {allowed ? (
          named(entry.label, inner)
        ) : (
          <Tooltip title={`Needs ${entry.scope}`} placement="right">
            <span>{inner}</span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const pageList = (pages: readonly PageDef[]): React.JSX.Element => (
    <Box
      component="ul"
      sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "2px" }}
    >
      {pages.map(pageRow)}
    </Box>
  );

  /** A heading with its pages under it. The heading leads to the first one you may open. */
  const sectionBlock = (name: string, pages: readonly PageDef[]): React.JSX.Element => {
    const Icon = SECTION_ICONS[name];
    const within = pages.some(
      (each) => pathname === (each.href ?? `/tenants/${tenant}/${each.page}`),
    );
    // Narrowed there is no heading to click, so the pages below it always show: a section shut
    // while its control is invisible could not be opened again.
    const open = collapsed || !shut.has(name);
    const listId = `nav-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
    // The first page here the caller may open, which is where the narrowed glyph leads.
    const first = pages.find((each) => each.scope === undefined || scopes.includes(each.scope));
    const href =
      first === undefined ? undefined : (first.href ?? `/tenants/${tenant}/${first.page}`);

    // Narrowed, the group is one glyph: the heading's. Its pages are not drawn, because a page
    // under a heading carries no glyph of its own and three blank strips are not a menu — so
    // the heading becomes the way in, and opens the first page the caller may use.
    if (collapsed) {
      return (
        <Box component="li" key={name}>
          {named(
            name,
            <Box
              component={href === undefined ? "span" : Link}
              {...(href === undefined ? {} : { to: href })}
              aria-current={within ? "page" : undefined}
              sx={{
                ...row,
                ...narrow,
                cursor: href === undefined ? "default" : "pointer",
                color: within ? tokens.accent.ink : tokens.ink.muted,
                bgcolor: within ? tokens.accent.soft : "transparent",
                ...(href === undefined || within
                  ? {}
                  : { "&:hover": { bgcolor: tokens.state.hover } }),
                "&:focus-visible": {
                  outline: `2px solid ${tokens.accent.base}`,
                  outlineOffset: "-2px",
                },
              }}
            >
              {Icon !== undefined && (
                <Box aria-hidden sx={{ flex: "none", display: "flex" }}>
                  <Icon size={18} strokeWidth={1.6} />
                </Box>
              )}
            </Box>,
          )}
        </Box>
      );
    }
    return (
      <Box component="li" key={name}>
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() =>
            setShut((closed) => {
              const next = new Set(closed);
              if (!next.delete(name)) next.add(name);
              return next;
            })
          }
          sx={{
            ...row,
            // Tighter than a page row: the chevron has to fit beside the longest heading
            // without the label turning into an ellipsis.
            gap: 2,
            pr: 1.5,
            bgcolor: "transparent",
            // The heading never takes the wash: the page under it does, and two marked rows
            // would read as two places rather than one inside the other.
            fontWeight: tokens.weight.medium,
            color: within ? tokens.ink.primary : tokens.ink.muted,
            "&:hover": { bgcolor: tokens.state.hover },
            "&:focus-visible": {
              outline: `2px solid ${tokens.accent.base}`,
              outlineOffset: "-2px",
            },
          }}
        >
          {Icon !== undefined && (
            <Box aria-hidden sx={{ flex: "none", display: "flex" }}>
              <Icon size={18} strokeWidth={1.6} />
            </Box>
          )}
          <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Box>
          <Box
            aria-hidden
            sx={{
              flex: "none",
              display: "flex",
              color: tokens.ink.faint,
              transform: open ? "none" : "rotate(-90deg)",
              transition: "transform .18s ease",
            }}
          >
            <ChevronDown size={14} strokeWidth={1.75} />
          </Box>
        </Box>
        {open && <Box id={listId}>{pageList(pages)}</Box>}
      </Box>
    );
  };

  /** Walks a group, gathering each run of pages that share a heading. */
  const rows = (all: readonly PageDef[]): React.JSX.Element[] => {
    // A page reached from a button on another page is routed and gated like any other, but it is
    // not a place the rail offers.
    const pages = all.filter((entry) => entry.listed !== false);
    const out: React.JSX.Element[] = [];
    for (let i = 0; i < pages.length; ) {
      const entry = pages[i];
      if (entry === undefined) break;
      if (entry.section === undefined) {
        out.push(pageRow(entry));
        i += 1;
        continue;
      }
      let end = i;
      while (end < pages.length && pages[end]?.section === entry.section) end += 1;
      out.push(sectionBlock(entry.section, pages.slice(i, end)));
      i = end;
    }
    return out;
  };

  return (
    <Box
      component="nav"
      aria-label="Console"
      sx={{
        width: collapsed ? 56 : 200,
        flex: "none",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        px: collapsed ? 1.5 : 3,
        pt: 1.5,
        pb: 3.5,
      }}
    >
      {GROUPS.filter((group) => !group.platform || isPlatform).map((group, index) => (
        <Box key={group.label}>
          {/* No eyebrow over the platform group: it holds one row that names itself, and a
              label for a single item is a label for nothing. */}
          {group.platform !== true && (
            <Eyebrow first={index === 0} hidden={collapsed}>
              {group.label}
            </Eyebrow>
          )}
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            {rows(group.pages)}
          </Box>
        </Box>
      ))}

      {/* The tenant, the account and the collapse control sit at the foot. The switcher is
          above the account because it governs everything over it, not the person under it. */}
      <Box sx={{ flex: 1, minHeight: 24 }} />
      {named(
        tenants.find((each) => each.id === tenant)?.name ?? "No tenants",
        <Box
          component="button"
          type="button"
          aria-haspopup={others.length === 0 ? undefined : "menu"}
          aria-expanded={others.length === 0 ? undefined : picking !== null}
          aria-label="Tenant"
          disabled={others.length === 0}
          onClick={(event) => setPicking(event.currentTarget)}
          sx={{
            ...row,
            ...(collapsed ? narrow : { gap: 2, pr: 1.5 }),
            color: tokens.ink.primary,
            fontWeight: tokens.weight.medium,
            bgcolor: picking !== null ? tokens.state.hover : "transparent",
            overflow: "hidden",
            cursor: others.length === 0 ? "default" : "pointer",
            ...(others.length === 0 ? {} : { "&:hover": { bgcolor: tokens.state.hover } }),
            "&:focus-visible": {
              outline: `2px solid ${tokens.accent.base}`,
              outlineOffset: "-2px",
            },
          }}
        >
          {/* A monogram, as the account below wears one: these two rows say *which* tenant and
              *which* person, not what a tenant or a person is. A rounded square against the
              account's circle is the old shorthand for an organisation against a human, so the
              pair is told apart by shape before either is read. */}
          <Box
            aria-hidden
            sx={{
              flex: "none",
              width: 18,
              height: 18,
              borderRadius: "4px",
              display: "grid",
              placeItems: "center",
              bgcolor: tokens.accent.soft,
              color: tokens.accent.ink,
              fontSize: 10,
              fontWeight: tokens.weight.semibold,
            }}
          >
            {(tenants.find((each) => each.id === tenant)?.name ?? "?").charAt(0).toUpperCase()}
          </Box>
          {!collapsed && (
            <>
              <Box
                sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}
              >
                {tenants.find((each) => each.id === tenant)?.name ?? "No tenants"}
              </Box>
              {others.length > 0 && (
                <Box aria-hidden sx={{ flex: "none", display: "flex", color: tokens.ink.faint }}>
                  <ChevronDown size={14} strokeWidth={1.75} />
                </Box>
              )}
            </>
          )}
        </Box>,
      )}
      <Menu
        anchorEl={picking}
        open={picking !== null}
        onClose={() => setPicking(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {tenants.map((each) => (
          <MenuItem
            key={each.id}
            selected={each.id === tenant}
            onClick={() => {
              setPicking(null);
              onSelectTenant(each.id);
            }}
          >
            {each.name}
            {/* The id earns its place only when it says something the name does not. A tenant
                called Shell whose id is `shell` would otherwise read as "Shell shell". */}
            {each.name.toLowerCase() !== each.id.toLowerCase() && (
              <Box
                component="span"
                sx={{ ml: 2, fontFamily: tokens.font.mono, fontSize: 11, color: tokens.ink.faint }}
              >
                {each.id}
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
      {named(
        account,
        <Box
          component="button"
          type="button"
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{
            ...row,
            ...(collapsed ? narrow : {}),
            color: tokens.ink.muted,
            bgcolor: anchor !== null ? tokens.state.hover : "transparent",
            overflow: "hidden",
            "&:hover": { bgcolor: tokens.state.hover },
            "&:focus-visible": {
              outline: `2px solid ${tokens.accent.base}`,
              outlineOffset: "-2px",
            },
          }}
        >
          <Box
            aria-hidden
            sx={{
              flex: "none",
              width: 18,
              height: 18,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: tokens.accent.soft,
              color: tokens.accent.ink,
              fontSize: 10,
              fontWeight: tokens.weight.semibold,
            }}
          >
            {account.charAt(0).toUpperCase()}
          </Box>
          {!collapsed && <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{account}</Box>}
        </Box>,
      )}

      {named(
        collapsed ? "Expand the sidebar" : "Collapse the sidebar",
        <Box
          component="button"
          type="button"
          aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
          aria-pressed={collapsed}
          onClick={onToggleCollapsed}
          sx={{
            ...row,
            ...(collapsed ? narrow : {}),
            color: tokens.ink.muted,
            bgcolor: "transparent",
            "&:hover": { bgcolor: tokens.state.hover },
            "&:focus-visible": {
              outline: `2px solid ${tokens.accent.base}`,
              outlineOffset: "-2px",
            },
          }}
        >
          <Box aria-hidden sx={{ flex: "none", display: "flex" }}>
            {collapsed ? (
              <PanelLeftOpen size={18} strokeWidth={1.6} />
            ) : (
              <PanelLeftClose size={18} strokeWidth={1.6} />
            )}
          </Box>
          {!collapsed && "Collapse"}
        </Box>,
      )}

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onAccount();
          }}
          sx={{ gap: 2.5 }}
        >
          <UserRound size={15} />
          Account
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSignOut();
          }}
          sx={{ gap: 2.5 }}
        >
          <LogOut size={15} />
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}
